import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { formatMs, formatUsd } from '../charts/chartUtils';

const ROLE_LABELS = {
  routed: 'Routed',
  original: 'Original',
  higher: 'Higher',
  lower: 'Lower',
};

const STATUS_META = {
  routed: { label: 'Routed', className: 'text-emerald-400' },
  cache_hit: { label: 'Cached', className: 'text-sky-400' },
  passthrough: { label: 'Passthrough', className: 'text-amber-400' },
  error: { label: 'Error', className: 'text-red-500' },
};

function statusKey(log) {
  if (log.decision === 'cache_hit' || log.cache_hit) return 'cache_hit';
  if (log.decision === 'local' || log.decision === 'fallback') return 'passthrough';
  if (log.decision === 'error') return 'error';
  return 'routed';
}

function scoreColor(score) {
  if (score == null) return 'opacity-50';
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  return 'text-red-400';
}

function evalComparison(delta) {
  if (delta == null) return null;
  if (delta > 2) return { label: 'better', className: 'text-emerald-400' };
  if (delta < -2) return { label: 'worse', className: 'text-red-400' };
  return { label: 'equal', className: 'opacity-60' };
}

function messageContent(content) {
  if (Array.isArray(content)) {
    return content.map((block) => block?.text || '').join(' ');
  }
  return content || '';
}

export default function LogDetailModal({
  detail,
  loading,
  tab,
  onTabChange,
  runningEval,
  evalError,
  onClose,
  onRunEval,
}) {
  if (loading && !detail) {
    return (
      <ModalShell onClose={onClose}>
        <p className="py-8 text-center opacity-50">Loading…</p>
      </ModalShell>
    );
  }

  if (!detail) return null;

  const meta = STATUS_META[statusKey(detail)] || STATUS_META.routed;
  const totalMs = (detail.latency_ms || 0) + (detail.generation_ms || 0);
  const hasInput = Boolean(detail.messages?.length || detail.prompt);
  const routedRun = detail.eval_runs?.find((r) => r.model_role === 'routed' && r.status === 'completed');
  const originalRun = detail.eval_runs?.find((r) => r.model_role === 'original' && r.status === 'completed');
  const evalDelta = routedRun?.score != null && originalRun?.score != null
    ? routedRun.score - originalRun.score
    : null;
  const comparison = evalComparison(evalDelta);

  return (
    <ModalShell onClose={onClose}>
      <div className="shrink-0">
        <div className="flex justify-between items-start mb-4 gap-4">
          <div>
            <h2 className="text-lg font-medium">
              {detail.input_model}
              {detail.output_model && detail.output_model !== detail.input_model && (
                <span className="opacity-60 font-normal"> → {detail.output_model}</span>
              )}
            </h2>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs mt-1 opacity-70">
              <span className={meta.className}>{meta.label}</span>
              {detail.upstream_provider && <span>via {detail.upstream_provider}</span>}
              <span>{new Date(detail.create_date).toLocaleString()}</span>
              {detail.key_name && <span>{detail.key_name}</span>}
              {detail.tier && <span>tier {detail.tier}</span>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="opacity-60 shrink-0">✕</button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-4 p-3 rounded-lg bg-theme-secondary/30">
          <Metric label="Total latency" value={formatMs(totalMs)} />
          <Metric label="Generation" value={formatMs(detail.generation_ms)} />
          <Metric label="Our overhead" value={formatMs(detail.latency_ms)} />
          <Metric label="Tokens" value={`${detail.tokens_in} / ${detail.tokens_out}`} />
          <Metric label="Final cost" value={formatUsd(detail.cost_usd)} />
          <Metric label="Original cost" value={formatUsd(detail.original_cost_usd)} />
          <Metric label="Savings" value={detail.savings_usd ? `${formatUsd(detail.savings_usd)} (${detail.savings_pct}%)` : '—'} />
          {comparison && (
            <Metric label="Eval vs original" value={comparison.label} className={comparison.className} />
          )}
        </div>

        <div className="flex gap-2 mb-4 border-b border-theme-secondary">
          {['request', 'evals'].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => onTabChange(key)}
              className={`px-3 py-2 text-sm capitalize border-b-2 -mb-px ${
                tab === key ? 'border-theme-primary text-theme-primary' : 'border-transparent opacity-60'
              }`}
            >
              {key}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {tab === 'request' && <RequestTab detail={detail} />}
        {tab === 'evals' && (
          <EvalsTab
            detail={detail}
            hasInput={hasInput}
            runningEval={runningEval}
            evalError={evalError}
            onRunEval={onRunEval}
          />
        )}
      </div>
    </ModalShell>
  );
}

function ModalShell({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden rounded-2xl bg-theme-main border border-theme-secondary p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function Metric({ label, value, className = '' }) {
  return (
    <div>
      <div className="opacity-60 mb-0.5">{label}</div>
      <div className={`font-mono ${className}`}>{value}</div>
    </div>
  );
}

function RequestTab({ detail }) {
  const params = detail.generation_params || {};
  const paramKeys = Object.keys(params);

  return (
    <div className="space-y-4 text-sm">
      {detail.messages?.length > 0 ? (
        <div>
          <div className="opacity-60 mb-2">Messages</div>
          <div className="space-y-2">
            {detail.messages.map((msg, i) => (
              <div key={i} className="rounded-lg bg-theme-secondary/30 p-3">
                <div className="text-xs uppercase opacity-50 mb-1">{msg.role}</div>
                <pre className="text-xs whitespace-pre-wrap">{messageContent(msg.content)}</pre>
              </div>
            ))}
          </div>
        </div>
      ) : detail.prompt ? (
        <div>
          <div className="opacity-60 mb-2">Prompt</div>
          <pre className="text-xs bg-theme-secondary/30 p-3 rounded-lg whitespace-pre-wrap">{detail.prompt}</pre>
        </div>
      ) : (
        <p className="opacity-50">No input stored.</p>
      )}

      {paramKeys.length > 0 && (
        <div>
          <div className="opacity-60 mb-2">Generation params</div>
          <pre className="text-xs bg-theme-secondary/30 p-3 rounded-lg whitespace-pre-wrap overflow-x-auto">
            {JSON.stringify(params, null, 2)}
          </pre>
        </div>
      )}

      <div>
        <div className="opacity-60 mb-2">Response</div>
        {detail.response ? (
          <pre className="text-xs bg-theme-secondary/30 p-3 rounded-lg whitespace-pre-wrap overflow-x-auto">{detail.response}</pre>
        ) : (
          <p className="opacity-50 text-xs">Response not stored.</p>
        )}
      </div>

      {detail.error && (
        <div>
          <div className="opacity-60 mb-2">Error</div>
          <pre className="text-xs text-red-500 whitespace-pre-wrap">{detail.error}</pre>
        </div>
      )}

      {detail.attempts?.length > 0 && (
        <div>
          <div className="opacity-60 mb-2">Attempts</div>
          <pre className="text-xs bg-theme-secondary/30 p-3 rounded-lg whitespace-pre-wrap overflow-x-auto">
            {JSON.stringify(detail.attempts, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function EvalsTab({
  detail,
  hasInput,
  runningEval,
  evalError,
  onRunEval,
}) {
  const runs = detail.eval_runs || [];
  const completedRuns = runs.filter((r) => r.status === 'completed');
  const routedRun = completedRuns.find((r) => r.model_role === 'routed');
  const originalRun = completedRuns.find((r) => r.model_role === 'original');
  const delta = routedRun?.score != null && originalRun?.score != null
    ? routedRun.score - originalRun.score
    : null;
  const comparison = evalComparison(delta);

  if (runningEval && !runs.length) {
    return (
      <div className="flex items-center gap-3 text-sm opacity-80 py-4">
        <span className="inline-block w-4 h-4 rounded-full border-2 border-theme-primary border-t-transparent animate-spin" />
        Running eval…
      </div>
    );
  }

  if (!runs.length) {
    return (
      <div className="text-sm">
        <p className="opacity-60 mb-4">No eval results for this request yet.</p>
        {evalError && <p className="text-red-500 mb-3">{evalError}</p>}
        {!detail.eval_enabled ? (
          <p className="opacity-50">Enable evals in project settings to run evals.</p>
        ) : !hasInput ? (
          <p className="opacity-50">No stored input to evaluate.</p>
        ) : (
          <button
            type="button"
            onClick={onRunEval}
            disabled={runningEval}
            className="px-4 py-2 rounded-lg border border-theme-secondary text-sm hover:bg-theme-secondary/30 disabled:opacity-40"
          >
            {runningEval ? 'Running eval…' : 'Run eval'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 text-sm overflow-visible">
      {comparison && (
        <p className="opacity-70">
          Routed model scored {routedRun.score}% vs original {originalRun.score}% ({comparison.label}).
        </p>
      )}
      <div className="overflow-visible rounded-xl border border-theme-secondary">
        <table className="w-full text-sm table-fixed">
          <thead>
            <tr className="border-b border-theme-secondary bg-theme-secondary/30 text-left">
              <th className="px-3 py-2 w-[38%]">Model</th>
              <th className="px-3 py-2 w-24">Role</th>
              <th className="px-3 py-2 w-20">Latency</th>
              <th className="px-3 py-2 w-24">Cost</th>
              <th className="px-3 py-2 w-28 text-right">Score</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.id} className="border-b border-theme-secondary/50 last:border-b-0">
                <td className="px-3 py-2 font-mono text-xs truncate">{run.model_tested}</td>
                <td className="px-3 py-2">{ROLE_LABELS[run.model_role] || run.model_role}</td>
                <td className="px-3 py-2">{run.latency_ms != null ? formatMs(run.latency_ms) : '—'}</td>
                <td className="px-3 py-2">{run.cost != null ? formatUsd(run.cost) : '—'}</td>
                <td className={`px-3 py-2 text-right whitespace-nowrap ${scoreColor(run.score)}`}>
                  <span className="inline-flex items-center justify-end gap-1">
                    <span>{run.score != null ? `${run.score}%` : run.status}</span>
                    {run.reasoning && (
                      <span className="relative inline-flex flex-none group/tip">
                        <InformationCircleIcon className="w-4 h-4 flex-none opacity-60 group-hover/tip:opacity-100" />
                        <span className="pointer-events-none absolute right-0 top-full mt-1.5 z-[100] hidden w-80 group-hover/tip:block rounded-lg border border-theme-secondary bg-theme-main p-3 text-left text-xs text-theme-text shadow-xl whitespace-pre-wrap">
                          {run.reasoning}
                        </span>
                      </span>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detail.eval_enabled && hasInput && (
        <button
          type="button"
          onClick={onRunEval}
          disabled={runningEval}
          className="px-4 py-2 rounded-lg border border-theme-secondary text-sm hover:bg-theme-secondary/30 disabled:opacity-40"
        >
          {runningEval ? 'Running eval…' : 'Run eval again'}
        </button>
      )}
      {evalError && <p className="text-red-500">{evalError}</p>}
    </div>
  );
}
